import { config } from '@uhg-abyss/web/tools/config';
import { storage } from '@uhg-abyss/web/tools/storage';
import { useEffect, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { Constants } from '../../common/Constants';
import { useNonGatedPcpStore } from '../../store/useNonGatedPcpStore';
import { NonGatedPcpState } from '../../store/useNonGatedPcpStore/nonGatedPcpStore';
import { isPcpSuppressionEligible } from '../../utils/pcpSuppression.utils';
import { getPCPGetParams } from '../../utils/primaryCare.utils';
import { normalizeProviderId } from '../../utils/string.utils';
import { getCurrentMember, getLoggedInMember } from '../../utils/user.utils';
import { useCustomQuery } from '../useCustomQuery';
import { useLagoon } from '../useLagoon';
import CLEAR_PRIMARY_CARE from './ClearPCPCache.graphql';
import DELETE_PRIMARY_CARE from './DeletePCP.graphql';
import GET_PRIMARY_CARE from './PrimaryCare.graphql';
import UPDATE_PRIMARY_CARE from './UpdatePCP.graphql';

export const useClearPrimaryCareCache = (options) =>
  useCustomQuery(CLEAR_PRIMARY_CARE, {
    ...options,
    url: config('GRAPHQL_API_URL'),
    clearCache: [
      {
        key: 'PrimaryCare',
        variables: [
          'dependentSeqNbr',
          'dateOfBirth',
          'enablePreProd',
          'familyId',
          'firstName',
          'groupNumber',
          'isGatedUser',
          'isNonGatedUSPUser',
          'lastName',
          'memberId',
          'id',
          'portalSource',
          'lob',
          'population',
          'membershipCategory',
          'claimSystemTypeCode',
          'eligibilitySystemTypeCode',
          'planVariationCode',
          'reciprocityId',
          'userName',
        ],
      },
    ],
    requestPolicy: 'no-cache',
    accessor: 'clearPCPCache',
  });

const usePrimaryCareQuery = (options) => {
  const [primaryCare, setPrimaryCare] = useState({});
  const [queryResult, queryAction] = useCustomQuery(GET_PRIMARY_CARE, {
    ...options,
    url: config('GRAPHQL_API_URL'),
    accessor: 'primaryCare',
    onCompleted: (result) => {
      if (options?.onCompleted) {
        options.onCompleted(result?.data);
      }
      setPrimaryCare(result?.data);
    },
    onError: (error) => {
      if (options?.onError) {
        options.onError(error);
      }
      setPrimaryCare({ hasPCPFetchError: true });
    },
  });

  const parsedResult = {
    ...queryResult,
    data: primaryCare,
  };

  return [parsedResult, queryAction];
};

const getNonDeletedPrimaryCareData = (data, dependentSeqNbr) => {
  const primaryCareEntry = data?.primaryCare?.find(
    ({ depSeqNbr, pcpInfo }) =>
      depSeqNbr === dependentSeqNbr &&
      Boolean(pcpInfo?.hasPCP && pcpInfo?.isDeleted !== true)
  );

  const pcpInfo = primaryCareEntry?.pcpInfo;
  const hasCurrentPCP = pcpInfo?.hasPCP && pcpInfo?.isDeleted !== true;

  if (!hasCurrentPCP) {
    return {};
  }

  return {
    ...(hasCurrentPCP
      ? {
          providerID: normalizeProviderId(pcpInfo.providerID),
          providerType: pcpInfo.providerType,
          providerName: pcpInfo?.pcpName ?? '',
          pcpId: pcpInfo?.pcpID,
        }
      : {}),
  };
};

export const usePrimaryCare = () => {
  const [memberId] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MEMBER_ID,
    0
  );

  const loggedInMember = getLoggedInMember();
  const dependentSeqNbr = loggedInMember?.demographics?.dependentSeqNbr;
  const currentMember = getCurrentMember();
  const featureFlags: [{ key: string; active: boolean }] = useLagoon(
    Constants.LAGOON_TABLE.FEATURE_FLAGS
  )();
  const enableNonGatedPcpSuppression =
    featureFlags.find((flag) => flag.key === 'ENABLE_NON_GATED_PCP_SUPPRESSION')
      ?.active ?? false;
  const isPcpSuppressed = isPcpSuppressionEligible(
    currentMember,
    enableNonGatedPcpSuppression
  );
  const {
    clearNonGatedPrimaryCareData,
    hasFetchedNonGatedPrimaryCareData,
    setHasFetchedNonGatedPrimaryCareData,
    setNonGatedPrimaryCareData,
  } = useNonGatedPcpStore(
    useShallow((state: NonGatedPcpState) => ({
      clearNonGatedPrimaryCareData: state.clearNonGatedPrimaryCareData,
      hasFetchedNonGatedPrimaryCareData:
        state.hasFetchedNonGatedPrimaryCareData,
      setHasFetchedNonGatedPrimaryCareData:
        state.setHasFetchedNonGatedPrimaryCareData,
      setNonGatedPrimaryCareData: state.setNonGatedPrimaryCareData,
    }))
  );

  const [primaryCare, getPrimaryCare] = usePrimaryCareQuery({
    onCompleted: (data) => {
      if (isPcpSuppressed) {
        const nonDeletedPrimaryCareData = getNonDeletedPrimaryCareData(
          data,
          dependentSeqNbr
        );

        if (Object.keys(nonDeletedPrimaryCareData).length) {
          setNonGatedPrimaryCareData(nonDeletedPrimaryCareData);
        } else {
          clearNonGatedPrimaryCareData();
        }
        setHasFetchedNonGatedPrimaryCareData(true);
      }
    },
    onError: () => {
      // Even on error, mark as fetched for non-gated members so getCareTeamPaginated can proceed
      if (isPcpSuppressed) {
        clearNonGatedPrimaryCareData();
        setHasFetchedNonGatedPrimaryCareData(true);
      }
    },
  });

  const { USE_PRE_PROD, IS_PREPROD_USER } = Constants.STORAGE_KEYS.SESSION;

  const [enablePreProd] = useState(storage.session.get(USE_PRE_PROD));
  useEffect(() => {
    storage.session.set(USE_PRE_PROD, enablePreProd);
  }, [enablePreProd]);
  const isCnSMember = currentMember?.isCnSMember;
  const isIFPMember = currentMember?.isIFPMember;
  const viewPcp = loggedInMember?.viewPcp;
  const shouldFetchPrimaryCare = isPcpSuppressed
    ? !hasFetchedNonGatedPrimaryCareData
    : viewPcp;

  useEffect(() => {
    if (
      loggedInMember &&
      shouldFetchPrimaryCare &&
      ((!isCnSMember && !isIFPMember) || memberId)
    ) {
      const variables = getPCPGetParams(
        storage.session.get(IS_PREPROD_USER),
        loggedInMember,
        memberId?.toString()
      );
      getPrimaryCare({ variables } as any);
    }
  }, [loggedInMember, memberId, shouldFetchPrimaryCare]);

  return isPcpSuppressed ? { ...primaryCare, data: {} } : primaryCare;
};

export const useUpdatePrimaryCare = (options) =>
  useCustomQuery(UPDATE_PRIMARY_CARE, {
    ...options,
    url: config('GRAPHQL_API_URL'),
    requestPolicy: 'no-cache',
    accessor: 'updatePCP',
  });

export const useDeletePrimaryCare = (options) =>
  useCustomQuery(DELETE_PRIMARY_CARE, {
    ...options,
    url: config('GRAPHQL_API_URL'),
    requestPolicy: 'no-cache',
    accessor: 'deletePCP',
  });
